// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogProps,
    DialogSurface,
} from '@fluentui/react-components';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { WarningImage } from '../../icons/status';
import { useAppStyles } from '../../styles';
import { TrackContext, TrackedLink } from '../common';
import { CancelButton, ConfirmButton, ConfirmationField } from '../dialogs';
import DialogHeader from '../dialogs/dialog-header';

// Delete activity confirmation dialog properties.
export type DeleteActivityDialogProps = Readonly<
    Partial<DialogProps> & {
        title: string;
        repoName?: string;
        onDelete: () => void;
    }
>;

// Delete activity confirmation dialog.
export const DeleteActivityDialog = (props: DeleteActivityDialogProps) => {
    const { title, onDelete, open, repoName } = props;
    const [confirmed, setConfirmed] = useState(false);
    const styles = useAppStyles();

    return (
        <TrackContext.Provider value={{ container: 'delete-activity-dialog' }}>
            <Dialog {...props}>
                <DialogSurface>
                    <DialogBody>
                        <DialogHeader
                            title={
                                <FormattedMessage
                                    description="Delete activity dialog title."
                                    defaultMessage="Delete activity"
                                />
                            }
                        />
                        <DialogContent className={styles.flexRowCentered}>
                            <WarningImage className={styles.dialogImage} />
                            <div className={styles.dialogContent}>
                                <div>
                                    <FormattedMessage
                                        description="Confirmation text on delete activity dialog."
                                        defaultMessage="Are you sure you want to delete activity ''{ACTIVITY_TITLE}''?"
                                        values={{ ACTIVITY_TITLE: title }}
                                    />
                                </div>
                                <div>
                                    <FormattedMessage
                                        description="Title for a list of items that will be lost when activity is deleted."
                                        defaultMessage="The following will be permanently deleted:"
                                    />
                                    <ul>
                                        <li>
                                            <FormattedMessage
                                                description="An item in the list of things that will be lost when deleting a activity."
                                                defaultMessage="Your published course content and files"
                                            />
                                        </li>
                                        <li>
                                            <FormattedMessage
                                                description="An item in the list of things that will be lost when deleting a activity."
                                                defaultMessage="All student progress and activity work"
                                            />
                                        </li>
                                    </ul>
                                    {repoName !== undefined && (
                                        <FormattedMessage
                                            description="Text on activity confirmation dialog."
                                            defaultMessage="Your {REPO_LINK} will remain available. If you re-import the course, a new URL will be generated and prior student progress will not be retained."
                                            values={{
                                                REPO_LINK: (
                                                    <TrackedLink
                                                        trackAction="github-settings"
                                                        href={`https://github.com/${repoName}/settings`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <FormattedMessage
                                                            description="Link text on the delete activity dialog."
                                                            defaultMessage="GitHub repository"
                                                        />
                                                    </TrackedLink>
                                                ),
                                            }}
                                        />
                                    )}
                                    <br />
                                    <br />
                                    <div className={styles.textSemibold}>
                                        <FormattedMessage
                                            description="Bolded warning text on the delete activity dialog."
                                            defaultMessage="Once deleted, your activity cannot be recovered!"
                                        />
                                    </div>
                                    <br />
                                    <ConfirmationField
                                        dialogOpen={open}
                                        onChange={setConfirmed}
                                        confirmationText="DELETE"
                                    />
                                </div>
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <CancelButton />
                            <ConfirmButton className={styles.buttonDanger} onClick={onDelete} disabled={!confirmed}>
                                <FormattedMessage
                                    description="Delete activity dialog confirmation button text."
                                    defaultMessage="Delete activity"
                                />
                            </ConfirmButton>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </TrackContext.Provider>
    );
};

export default DeleteActivityDialog;
